import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import GpsMap from "@/components/GpsMap";

type Profile = {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  location: string | null;
};

export default function Dashboard() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("full_name, email, phone, company, location")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile(data));
  }, [user]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-xl font-bold text-primary">
            Mowzi
          </Link>
          <Button variant="ghost" size="sm" onClick={() => signOut().then(() => navigate("/"))}>
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-10">
        <h1 className="font-display text-2xl font-bold">
          Welcome{profile?.full_name ? `, ${profile.full_name}` : ""}
        </h1>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
            <p>
              <span className="text-muted-foreground">Email: </span>
              {profile?.email ?? user.email}
            </p>
            <p>
              <span className="text-muted-foreground">Phone: </span>
              {profile?.phone || "—"}
            </p>
            <p>
              <span className="text-muted-foreground">Company: </span>
              {profile?.company || "—"}
            </p>
            <p>
              <span className="text-muted-foreground">Location: </span>
              {profile?.location || "—"}
            </p>
          </CardContent>
        </Card>

        <GpsMap />
      </main>
    </div>
  );
}
